import React from "react";
import { BannerLogoProps } from "./BannerLogo.types";
import { MapTo } from "@adobe/aem-react-editable-components";
import { RangoButton, RangoHeading, RangoParagraph } from "@rango/core";
import { BannerContainer, ImageContainer } from "./BannerLogo.styles";
import { useIsMobile } from "src/utils/js/useIsMobile";
import { useAleloContext } from "src/context/aleloContext";
import sanitizeHtml from "sanitize-html";
import sanitizeHtmlWhitelist from "src/components/sanitize-html.whitelist";

const BannerLogo: React.FC<BannerLogoProps> = ({
	heading,
	paragraph,
	buttonText,
	dataParent,
	colorHeading,
	colorParagraph,
	colorButton,
	backgroundColorImage,
	backgroundColor,
	backgroundImage,
	backgroundImageAlt,
	hideBackgroundImage,
	logoImage,
	logoImageAlt,
	actionType,
	modalTarget,
	step,
	anchorSection,
	linkUrl,
	target,
	id,
}) => {
	const { setCurrentModal } = useAleloContext();

	function openUrl(linkUrl: string, target: string) {
		window.open(linkUrl, target, "noreferrer");
		return null;
	}

	function scrollToSection(anchorSection: string) {
		const section = document.getElementById(anchorSection);
		if (section) section.scrollIntoView({ behavior: "smooth" });
	}

	const tags = {
		element_parent: dataParent || "banner-with-logo",
		element_type: actionType === "link" ? "link" : "button",
		destination: actionType === "link" ? linkUrl || "" : modalTarget || "",
		title: buttonText,
	};

	const { isMobile, isTablet } = useIsMobile();

	const handleClick = () => {
		if (actionType === "link") {
			openUrl(linkUrl, target);
		} else if (actionType === "modal") {
			setCurrentModal({ modal: modalTarget, step: step });
		} else scrollToSection(anchorSection);
	};

	const buttonStyles = {
		backgroundColor: backgroundColor,
		color: colorButton,
	};

	return (
		<>
			{heading && (
				<BannerContainer
					backgroundColor={backgroundColorImage}
					colorParagraph={colorParagraph}
					id={id}
				>
					<div className="banner-logo-left-side">
						{logoImage && (
							<div className="banner-logo-img">
								<img
									src={logoImage}
									alt={logoImageAlt}
									style={{
										height: isMobile || isTablet ? "34px" : "60px",
									}}
									loading="lazy"
								/>
							</div>
						)}

						<div className="banner-content">
							<RangoHeading
								tag="h2"
								style={{
									color: colorHeading,
									textAlign: "left",
									paddingTop: logoImage || isMobile ? "0px" : "100px",
								}}
							>
								{heading}
							</RangoHeading>

							{!hideBackgroundImage && isMobile ? (
								<div>
									<img
										className="bannerImg__mobile"
										src={backgroundImage}
										alt={backgroundImageAlt}
										loading="lazy"
									/>
								</div>
							) : null}

							<RangoParagraph
								dangerouslySetInnerHTML={{
									__html: sanitizeHtml(paragraph, sanitizeHtmlWhitelist),
								}}
								style={{
									color: colorParagraph,
									textAlign: "left",
								}}
							/>

							{buttonText && (
								<RangoButton
									size={"sm"}
									{...tags}
									title={buttonText}
									onClick={() => handleClick()}
									style={buttonStyles}
								>
									{buttonText}
								</RangoButton>
							)}
						</div>
					</div>

					{backgroundImage ? (
						<ImageContainer
							backgroundImage={backgroundImage}
							title={backgroundImageAlt}
							style={{
								display: isMobile ? "none" : "block",
							}}
						/>
					) : null}
				</BannerContainer>
			)}
		</>
	);
};

const BannerLogoEditConfig = {
	emptyLabel: "Banner Logo",
	isEmpty: function (props: BannerLogoProps) {
		return !props || !props.heading;
	},
};

MapTo<BannerLogoProps>("alelo/components/content/v1/banner-logo")(
	BannerLogo,
	BannerLogoEditConfig
);

export default BannerLogo;
